import {
	EmptyState,
	LoadingState,
	PageContainer,
	PageHeader,
	StatusBadge,
} from "@client/components/ui";
import { Button } from "@client/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@client/components/ui/card";
import { trpc } from "@client/lib/trpc-client";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useRealtimeRun } from "@trigger.dev/react-hooks";
import {
	CheckCircle,
	Clock,
	ExternalLink,
	Globe,
	Loader2,
	RefreshCw,
	Search,
	XCircle,
	Zap,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

export const Route = createFileRoute("/(protected)/jobs")({
	component: JobsPage,
});

interface ActivityWithRealtime {
	id: string;
	url: string;
	status: string;
	createdAt: string;
	activityDate: string;
	type: "generation" | "discovery";
	// Generation specific
	completedAt?: string | null;
	progress?: string;
	error?: string | null;
	creditsUsed?: number;
	totalUrlsFound?: number;
	urlsScraped?: number;
	// Discovery specific
	discoveryType?: string;
	creditsCost?: number;
	siteId?: string | null;
	// Realtime fields
	firecrawlJobId?: string | null;
	publicAccessToken?: string | null;
}

// Hook to track a single job's realtime status
function useJobRealtimeStatus(activity: ActivityWithRealtime) {
	const hasRealtimeAccess = !!(
		activity.firecrawlJobId && activity.publicAccessToken
	);

	const { run } = useRealtimeRun(activity.firecrawlJobId ?? "", {
		enabled:
			hasRealtimeAccess &&
			(activity.status === "processing" || activity.status === "queued"),
		accessToken: activity.publicAccessToken ?? undefined,
	});

	const realtimeStatus = useMemo(() => {
		if (!run) return null;
		return run.status ? String(run.status).toLowerCase() : null;
	}, [run]);

	const realtimeProgress = useMemo(() => {
		if (!run?.metadata) return null;
		const progress = run.metadata.progress;
		if (typeof progress === "number") return progress;
		if (typeof progress === "string") return Number.parseInt(progress, 10);
		return null;
	}, [run?.metadata]);

	const isCompleted = useMemo(() => {
		if (!run) return false;
		const status = run.status ? String(run.status).toLowerCase() : undefined;
		return (
			status === "completed" || status === "failed" || status === "canceled"
		);
	}, [run]);

	return { realtimeStatus, realtimeProgress, isCompleted, hasRealtimeAccess };
}

function JobActivityItem({
	activity,
	onRealtimeComplete,
}: {
	activity: ActivityWithRealtime;
	onRealtimeComplete?: (jobId: string) => void;
}) {
	const { realtimeStatus, realtimeProgress, isCompleted, hasRealtimeAccess } =
		useJobRealtimeStatus(activity);

	// Notify parent when job completes via realtime
	useEffect(() => {
		if (isCompleted && onRealtimeComplete) {
			onRealtimeComplete(activity.id);
		}
	}, [isCompleted, activity.id, onRealtimeComplete]);

	// Use realtime status if available, otherwise fall back to database status
	const effectiveStatus =
		realtimeStatus === "completed" || realtimeStatus === "failed"
			? realtimeStatus
			: activity.status;

	const effectiveProgress =
		realtimeProgress !== null ? String(realtimeProgress) : activity.progress;

	const formatTime = (dateString: string) => {
		const date = new Date(dateString);
		const now = new Date();
		const diffInMinutes = Math.floor(
			(now.getTime() - date.getTime()) / (1000 * 60),
		);

		if (diffInMinutes < 1) return "Just now";
		if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
		if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
		return date.toLocaleDateString();
	};

	const getStatusIcon = (type: string, status: string) => {
		if (type === "discovery") {
			switch (status) {
				case "completed":
					return <Search className="h-4 w-4 text-green-500" />;
				case "failed":
					return <XCircle className="h-4 w-4 text-red-500" />;
				case "processing":
					return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
				default:
					return <Clock className="h-4 w-4 text-orange-500" />;
			}
		}

		// Generation jobs
		switch (status) {
			case "completed":
				return <CheckCircle className="h-4 w-4 text-green-500" />;
			case "failed":
				return <XCircle className="h-4 w-4 text-red-500" />;
			case "processing":
				return <Loader2 className="h-4 w-4 animate-spin text-blue-500" />;
			default:
				return <Clock className="h-4 w-4 text-orange-500" />;
		}
	};

	const getStatusText = (type: string, status: string) => {
		if (type === "discovery") {
			switch (status) {
				case "completed":
					return "URL Discovery Complete";
				case "failed":
					return "Discovery Failed";
				case "processing":
					return "Discovering URLs";
				default:
					return "Discovery Queued";
			}
		}
		// Generation jobs
		switch (status) {
			case "completed":
				return "Generation Complete";
			case "failed":
				return "Generation Failed";
			case "processing":
				return "Generating";
			default:
				return "Generation Queued";
		}
	};

	const getStatusBadgeVariant = (status: string) => {
		switch (status) {
			case "completed":
				return "completed";
			case "failed":
				return "failed";
			case "processing":
				return "active";
			default:
				return "pending";
		}
	};

	const getActivityLink = () => {
		if (activity.type === "generation") {
			return `/generation/${activity.id}`;
		}
		// Discovery - link to discovery status page
		return `/discovery/${activity.id}`;
	};

	return (
		<div className="flex items-center justify-between rounded-lg border p-4 transition-colors hover:bg-muted/50 dark:border-gray-700 dark:hover:bg-gray-800/50">
			<div className="min-w-0 flex-1">
				<div className="flex items-center gap-3">
					{getStatusIcon(activity.type, effectiveStatus)}
					<div className="min-w-0 flex-1">
						<div className="flex flex-wrap items-center gap-2">
							<span className="truncate font-medium text-sm dark:text-gray-200">
								{new URL(activity.url).hostname}
							</span>
							<StatusBadge variant={getStatusBadgeVariant(effectiveStatus)}>
								{getStatusText(activity.type, effectiveStatus)}
							</StatusBadge>
							{activity.type === "discovery" && activity.discoveryType && (
								<StatusBadge
									variant={
										activity.discoveryType === "first_time" ? "free" : "active"
									}
								>
									{activity.discoveryType === "first_time" ? "Free" : "Rescan"}
								</StatusBadge>
							)}
							{hasRealtimeAccess &&
								(effectiveStatus === "processing" ||
									effectiveStatus === "queued") && (
									<span className="flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-green-800 text-xs dark:bg-green-900/30 dark:text-green-300">
										<Zap className="h-3 w-3" />
										Live
									</span>
								)}
						</div>
						<div className="flex items-center gap-2 text-muted-foreground text-xs dark:text-gray-400">
							<Globe className="h-3 w-3 shrink-0" />
							<span className="truncate">{activity.url}</span>
						</div>
						<div className="mt-2 flex flex-wrap items-center gap-4 text-xs">
							<span className="text-muted-foreground dark:text-gray-400">
								{formatTime(activity.activityDate)}
							</span>
							{activity.type === "generation" &&
								effectiveStatus === "processing" &&
								effectiveProgress && (
									<span className="text-blue-600 dark:text-blue-400">
										{effectiveProgress}% complete
									</span>
								)}
							{activity.type === "generation" &&
								effectiveStatus === "completed" &&
								activity.creditsUsed &&
								activity.creditsUsed > 0 && (
									<span className="text-muted-foreground dark:text-gray-400">
										{activity.creditsUsed} credits used
									</span>
								)}
							{activity.type === "discovery" &&
								effectiveStatus === "completed" &&
								activity.totalUrlsFound &&
								activity.totalUrlsFound > 0 && (
									<span className="text-muted-foreground dark:text-gray-400">
										{activity.totalUrlsFound} URLs found
									</span>
								)}
						</div>
					</div>
				</div>
			</div>
			<div className="ml-4 flex shrink-0 items-center gap-1">
				<Button asChild variant="ghost" size="sm">
					<Link to={getActivityLink()}>
						<ExternalLink className="h-4 w-4" />
					</Link>
				</Button>
			</div>
		</div>
	);
}

function JobsPage() {
	const [limit, setLimit] = useState(20);
	const [completedJobIds, setCompletedJobIds] = useState<Set<string>>(
		new Set(),
	);
	const trpcUtils = trpc.useUtils();

	const activitiesQuery = trpc.sites.getRecentActivities.useQuery({
		limit,
	});

	// Handle when a job completes via realtime
	const handleRealtimeComplete = useCallback(
		(jobId: string) => {
			setCompletedJobIds((prev) => {
				if (prev.has(jobId)) return prev;
				const newSet = new Set(prev);
				newSet.add(jobId);
				return newSet;
			});

			// Refresh data after a short delay
			setTimeout(() => {
				trpcUtils.sites.getRecentActivities.invalidate();
			}, 1000);
		},
		[trpcUtils],
	);

	// Filter to only show job activities (not sites)
	// Also update status for jobs that completed via realtime
	const jobActivities = useMemo(() => {
		const activities =
			activitiesQuery.data?.filter(
				(activity) =>
					activity.type === "generation" || activity.type === "discovery",
			) || [];

		// Mark jobs as completed if they finished via realtime
		return activities.map((activity) => {
			if (
				completedJobIds.has(activity.id) &&
				activity.status !== "completed" &&
				activity.status !== "failed"
			) {
				return { ...activity, status: "completed" };
			}
			return activity;
		});
	}, [activitiesQuery.data, completedJobIds]);

	const handleRefresh = () => {
		activitiesQuery.refetch();
	};

	const handleLoadMore = () => {
		setLimit((prev) => prev + 20);
	};

	return (
		<PageContainer maxWidth="full">
			<PageHeader
				title="All Jobs"
				description="View and manage your generation jobs and URL discoveries"
				actions={
					<Button
						onClick={handleRefresh}
						variant="outline"
						size="sm"
						disabled={activitiesQuery.isLoading}
					>
						<RefreshCw
							className={`mr-2 h-4 w-4 ${activitiesQuery.isLoading ? "animate-spin" : ""}`}
						/>
						Refresh
					</Button>
				}
			/>

			<Card className="border-2 dark:border-gray-700">
				<CardHeader>
					<CardTitle className="dark:text-gray-100">All Jobs</CardTitle>
					<CardDescription className="dark:text-gray-400">
						{jobActivities.length
							? `Showing ${jobActivities.length} job${jobActivities.length === 1 ? "" : "s"} (generation and discovery)`
							: "Your generation jobs and URL discoveries will appear here"}
					</CardDescription>
				</CardHeader>
				<CardContent>
					{activitiesQuery.isLoading ? (
						<LoadingState variant="inline" message="Loading jobs..." />
					) : activitiesQuery.isError ? (
						<div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center dark:border-red-900 dark:bg-red-950/30">
							<h3 className="mb-2 font-semibold text-lg text-red-900 dark:text-red-200">
								Error loading jobs
							</h3>
							<p className="mb-4 text-red-800 text-sm dark:text-red-300">
								There was a problem loading your jobs.
							</p>
							<Button
								onClick={() => activitiesQuery.refetch()}
								variant="secondary"
								size="sm"
							>
								Try Again
							</Button>
						</div>
					) : jobActivities.length === 0 ? (
						<EmptyState
							icon={Clock}
							title="No jobs yet"
							description="Add a site to get started with URL discovery and LLMs.txt generation."
							action={{
								label: "Add Your First Site",
								onClick: () => {},
								icon: Globe,
							}}
						/>
					) : (
						<div className="space-y-3">
							{jobActivities.map((activity) => (
								<JobActivityItem
									key={`${activity.type}-${activity.id}`}
									activity={activity as ActivityWithRealtime}
									onRealtimeComplete={handleRealtimeComplete}
								/>
							))}
						</div>
					)}

					{/* Load More Button */}
					{jobActivities.length >= limit && (
						<div className="mt-6 text-center">
							<Button
								onClick={handleLoadMore}
								variant="outline"
								disabled={activitiesQuery.isLoading}
							>
								Load More Jobs
							</Button>
						</div>
					)}
				</CardContent>
			</Card>
		</PageContainer>
	);
}
